import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  ScanLine,
  Search,
  Filter,
  X,
  Trash2,
  Printer,
  Loader2,
  AlertCircle,
  Inbox,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  FileText,
  Upload,
} from "lucide-react";import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const emptyFilters = {
  search: "",
  sent_to: "",
  date_from: "",
  date_to: "",
  time_from: "",
  time_to: "",
};

const ScanIqPage = () => {
  const { canEdit, isAdmin } = useAuth();

  // Input (uncommitted) state
  const [inputs, setInputs] = useState(emptyFilters);
  // Applied (committed) query state that actually drives fetching
  const [query, setQuery] = useState(emptyFilters);

  const [scans, setScans] = useState([]);
  const [summary, setSummary] = useState({ total: 0, duplicates: 0, unique_items: 0 });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [importing, setImporting] = useState(false);
  const [overwriteImport, setOverwriteImport] = useState(false);
  const fileInputRef = useRef(null);

  const filtersActive =
    !!query.search || !!query.sent_to || !!query.date_from || !!query.date_to || !!query.time_from || !!query.time_to;

  const buildParams = useCallback(
    (q, extra = {}) => {
      const p = { page: 1, page_size: PAGE_SIZE, sort: "desc", ...extra };
      if (q.search) p.search = q.search;
      if (q.sent_to) p.sent_to = q.sent_to;
      if (q.date_from) p.date_from = q.date_from;
      if (q.date_to) p.date_to = q.date_to;
      if (q.time_from) p.time_from = q.time_from;
      if (q.time_to) p.time_to = q.time_to;
      return p;
    },
    []
  );

  const fetchScans = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const { data } = await axios.get(`${API}/scan-iq/scans`, {
        params: buildParams(query, { page, page_size: PAGE_SIZE }),
      });
      setScans(data.scans || []);
      setSummary(data.summary || { total: 0, duplicates: 0, unique_items: 0 });
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setLastUpdated(new Date());
    } catch (err) {
      if (!silent) setError(err.response?.data?.detail || "Failed to load scans");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [query, page, buildParams]);

  useEffect(() => {
    fetchScans();
  }, [fetchScans]);

  // Live polling: silently refetch the current view so new scans appear
  // without a manual reload. Paused while a delete dialog is open or the
  // tab is hidden.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const iv = setInterval(() => {
      if (!toDelete && (typeof document === "undefined" || !document.hidden)) {
        fetchScans(true);
      }
    }, 10000);
    return () => clearInterval(iv);
  }, [autoRefresh, toDelete, fetchScans]);

  const applyFilters = () => {
    setPage(1);
    setQuery({ ...inputs });
  };

  const clearFilters = () => {
    setInputs(emptyFilters);
    setQuery(emptyFilters);
    setPage(1);
  };

  const onSearchKey = (e) => {
    if (e.key === "Enter") applyFilters();
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/scan-iq/scans/${toDelete.id}`);
      toast.success("Scan deleted");
      setToDelete(null);
      fetchScans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete scan");
      setToDelete(null);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/scan-iq/export`, {
        params: buildParams(query, { page: 1, page_size: 10000 }),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "scan-iq-export.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("overwrite", overwriteImport ? "true" : "false");
      const { data } = await axios.post(`${API}/scan-iq/import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parts = [`${data.added} added`, `${data.updated} updated`, `${data.skipped} unchanged`];
      if (data.error_count) parts.push(`${data.error_count} skipped`);
      toast.success(`Import complete — ${parts.join(", ")}`);
      fetchScans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handlePrint = async () => {
    setPrinting(true);
    try {
      // Always print the FULL matching result set (not just current page)
      const { data } = await axios.get(`${API}/scan-iq/scans`, {
        params: buildParams(query, { page: 1, page_size: 5000 }),
      });
      const rows = data.scans || [];
      if (rows.length === 0) {
        toast.info("Nothing to print for the current view");
        return;
      }
      if (rows.length > 500 && !window.confirm(`This will print ${rows.length} scan records. Continue?`)) {
        return;
      }
      openPrintWindow(rows, query, data.summary);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to prepare print view");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-50 flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-teal-600 rounded-xl flex items-center justify-center shadow-md">
              <ScanLine className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-brand-900">Scan-IQ</h1>
              <p className="text-sm text-brand-500">
                Live barcode scans from the shop floor. Duplicates are flagged in red.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh((v) => !v)}
              className={autoRefresh ? "text-emerald-700 border-emerald-300 bg-emerald-50" : ""}
              data-testid="toggle-auto-refresh-btn"
              title={autoRefresh ? "Live updates on (every 10s)" : "Live updates off"}
            >
              <span
                className={`w-2 h-2 rounded-full mr-2 ${autoRefresh ? "bg-emerald-500 animate-pulse" : "bg-gray-400"}`}
              />
              {autoRefresh ? "Live" : "Paused"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchScans(false)}
              disabled={loading}
              data-testid="refresh-scans-btn"
              title="Refresh now"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              onClick={handleExport}
              variant="outline"
              disabled={loading || total === 0}
              data-testid="export-scans-btn"
            >
              <FileText className="w-4 h-4 mr-2" /> Export
            </Button>
            {isAdmin && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={handleImport}
                  data-testid="import-scans-input"
                />
                <label
                  className="flex items-center gap-1.5 text-xs text-brand-600 select-none cursor-pointer px-1"
                  title="When on, imported rows overwrite the SPS Tag / Area / Rev of scans with the same Short Code (blank cells never clear existing values)."
                  data-testid="overwrite-import-label"
                >
                  <input
                    type="checkbox"
                    checked={overwriteImport}
                    onChange={(e) => setOverwriteImport(e.target.checked)}
                    className="accent-teal-600"
                    data-testid="overwrite-import-checkbox"
                  />
                  Overwrite
                </label>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  disabled={importing || loading}
                  data-testid="import-scans-btn"
                >
                  {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Import
                </Button>
              </>
            )}
            <Button
              onClick={handlePrint}
              disabled={printing || loading || total === 0}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="print-scans-btn"
            >
              {printing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
              Print
            </Button>
          </div>
        </div>

        {/* Summary chips */}
        <div className="flex flex-wrap gap-3 mb-5">
          <Badge className="bg-brand-100 text-brand-700" data-testid="summary-total">
            {summary.total} scan{summary.total === 1 ? "" : "s"}
          </Badge>
          <Badge className="bg-red-100 text-red-800 border-red-200" data-testid="summary-duplicates">
            {summary.duplicates} duplicate record{summary.duplicates === 1 ? "" : "s"}
          </Badge>
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="summary-unique">
            {summary.unique_items} unique item{summary.unique_items === 1 ? "" : "s"}
          </Badge>
          {lastUpdated && (
            <span className="text-xs text-brand-400 self-center ml-auto" data-testid="last-updated">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>

        {/* Search + Filters */}
        <div className="bg-white border border-brand-200 rounded-xl p-4 mb-6">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-400" />
            <Input
              value={inputs.search}
              onChange={(e) => setInputs({ ...inputs, search: e.target.value })}
              onKeyDown={onSearchKey}
              placeholder="Search ShortSupportNumber or SupportNo (partial match, e.g. PF-105 or 00-S-02)"
              className="pl-9"
              data-testid="scan-search-input"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-brand-600 mb-1 block">Sent to</label>
              <Input
                type="text"
                placeholder="e.g. Dublin Depot"
                value={inputs.sent_to}
                onChange={(e) => setInputs({ ...inputs, sent_to: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
                data-testid="filter-sent-to"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-brand-600 mb-1 block">Date From</label>
              <Input
                type="date"
                value={inputs.date_from}
                onChange={(e) => setInputs({ ...inputs, date_from: e.target.value })}
                data-testid="filter-date-from"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-brand-600 mb-1 block">Date To</label>
              <Input
                type="date"
                value={inputs.date_to}
                onChange={(e) => setInputs({ ...inputs, date_to: e.target.value })}
                data-testid="filter-date-to"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-brand-600 mb-1 block">Time From (UK)</label>
              <Input
                type="time"
                value={inputs.time_from}
                onChange={(e) => setInputs({ ...inputs, time_from: e.target.value })}
                data-testid="filter-time-from"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-brand-600 mb-1 block">Time To (UK)</label>
              <Input
                type="time"
                value={inputs.time_to}
                onChange={(e) => setInputs({ ...inputs, time_to: e.target.value })}
                data-testid="filter-time-to"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <Button
              onClick={applyFilters}
              className="bg-brand-900 hover:bg-brand-800 text-white"
              data-testid="apply-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" /> Apply Filters
            </Button>
            <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
              <X className="w-4 h-4 mr-2" /> Clear Filters
            </Button>
            {filtersActive && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 ml-1" data-testid="active-filters-badge">
                Filters active
              </Badge>
            )}
          </div>
        </div>

        {/* Table / states */}
        <div className="bg-white border border-brand-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center" data-testid="scans-loading">
              <Loader2 className="w-7 h-7 animate-spin mx-auto text-brand-400" />
              <p className="text-sm text-brand-400 mt-3">Loading scans…</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center" data-testid="scans-error">
              <AlertCircle className="w-8 h-8 mx-auto text-red-500" />
              <p className="text-sm text-red-600 mt-3">{error}</p>
              <Button variant="outline" className="mt-4" onClick={fetchScans}>
                Retry
              </Button>
            </div>
          ) : scans.length === 0 ? (
            <div className="p-12 text-center" data-testid="scans-empty">
              <Inbox className="w-8 h-8 mx-auto text-brand-300" />
              <p className="text-sm text-brand-500 mt-3">
                {filtersActive
                  ? "No scans match the selected search or filters."
                  : "No scans have been recorded yet."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="scans-table">
                <thead className="bg-brand-100/50">
                  <tr className="text-left text-xs font-semibold text-brand-700">
                    <th className="px-4 py-2.5">Date</th>
                    <th className="px-4 py-2.5">Time</th>
                    <th className="px-4 py-2.5">ShortSupportNumber</th>
                    <th className="px-4 py-2.5">SupportNo</th>
                    <th className="px-4 py-2.5">Area</th>
                    <th className="px-4 py-2.5">Rev</th>
                    <th className="px-4 py-2.5">Sent to</th>
                    <th className="px-4 py-2.5">Bracket</th>
                    <th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scans.map((s) => (
                    <tr
                      key={s.id}
                      className={`border-t border-brand-100 ${
                        s.is_duplicate ? "bg-red-50 hover:bg-red-100/70" : "hover:bg-brand-50/40"
                      }`}
                      data-testid={`scan-row-${s.id}`}
                      data-duplicate={s.is_duplicate ? "true" : "false"}
                    >
                      <td className="px-4 py-2.5 text-brand-700 whitespace-nowrap">{s.date}</td>
                      <td className="px-4 py-2.5 text-brand-700 whitespace-nowrap">{s.time}</td>
                      <td className="px-4 py-2.5 font-medium text-brand-900">
                        <div className="flex items-center gap-2">
                          {s.short_code}
                          {s.is_duplicate && (
                            <Badge
                              className="bg-red-600 text-white border-red-700 text-[10px] px-1.5 py-0"
                              data-testid={`dup-badge-${s.id}`}
                              title={`Scanned ${s.duplicate_count} times`}
                            >
                              <AlertCircle className="w-3 h-3 mr-0.5" /> Duplicate ×{s.duplicate_count}
                            </Badge>
                          )}
                          {s.delivered && (
                            <Badge
                              className="bg-red-700 text-white border-red-800 text-[10px] px-1.5 py-0"
                              data-testid={`delivered-badge-${s.id}`}
                            >
                              Delivered
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-brand-700">{s.sps_tag}</td>
                      <td className="px-4 py-2.5 text-brand-700">{s.area}</td>
                      <td className="px-4 py-2.5 text-brand-700">{s.rev}</td>
                      <td className="px-4 py-2.5 text-brand-700" data-testid={`sent-to-${s.id}`}>{s.sent_to || <span className="text-brand-300">—</span>}</td>
                      <td className="px-4 py-2.5">
                        {s.bracket_sheet_id ? (
                          <Link
                            to={`/projects/${s.bracket_sheet_id}?support=${encodeURIComponent(s.bracket_support)}`}
                            className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-900 hover:underline font-medium"
                            data-testid={`bracket-link-${s.id}`}
                          >
                            <FileText className="w-3.5 h-3.5" /> Open
                          </Link>
                        ) : (
                          <span className="text-brand-400 text-xs" data-testid={`bracket-nolink-${s.id}`}>
                            Not linked
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {canEdit && (!s.delivered || isAdmin) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                            onClick={() => setToDelete(s)}
                            data-testid={`delete-scan-${s.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && scans.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-brand-200">
              <span className="text-xs text-brand-500" data-testid="pagination-info">
                Page {page} of {totalPages} · {total} total
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" /> Prev
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="next-page-btn"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />

      {/* Delete confirmation — user/admin initiated only */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-scan-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this scan record?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
              {toDelete && (
                <span className="block mt-2 font-mono text-xs text-brand-700">
                  {toDelete.short_code} | {toDelete.sps_tag} | {toDelete.area} | {toDelete.rev}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-scan">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-scan"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

function openPrintWindow(rows, q, summary) {
  const criteria = [];
  if (q.search) criteria.push(`Search: "${q.search}"`);
  if (q.sent_to) criteria.push(`Sent to: "${q.sent_to}"`);
  if (q.date_from) criteria.push(`Date from: ${q.date_from}`);
  if (q.date_to) criteria.push(`Date to: ${q.date_to}`);
  if (q.time_from) criteria.push(`Time from: ${q.time_from} UTC`);
  if (q.time_to) criteria.push(`Time to: ${q.time_to} UTC`);
  const criteriaLine = criteria.length ? criteria.join(" · ") : "No filters (all scans)";
  const printedAt = new Date().toLocaleString();

  const esc = (v) =>
    String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  const body = rows
    .map(
      (s) => `<tr class="${s.is_duplicate ? "dup" : ""}">
        <td>${esc(s.date)}</td><td>${esc(s.time)}</td><td>${esc(s.short_code)}${
        s.is_duplicate ? ` <span class="tag">DUPLICATE ×${esc(s.duplicate_count)}</span>` : ""
      }</td>
        <td>${esc(s.sps_tag)}</td><td>${esc(s.area)}</td><td>${esc(s.rev)}</td><td>${esc(s.sent_to)}</td>
      </tr>`
    )
    .join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Scan-IQ Report</title>
    <style>
      * { font-family: Arial, Helvetica, sans-serif; }
      body { margin: 24px; color: #111; }
      h1 { font-size: 20px; margin: 0 0 4px; }
      .meta { font-size: 12px; color: #444; margin-bottom: 2px; }
      table { width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 12px; }
      th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
      th { background: #1f4e79; color: #fff; }
      tr.dup td { background: #fde2e2; }
      .tag { background: #c0392b; color:#fff; font-size:9px; padding:1px 4px; border-radius:3px; }
      @media print { .no-print { display: none; } }
    </style></head><body>
      <img src="${window.location.origin}/nassau-logo.webp" alt="Nassau Engineering" style="height:56px;width:auto;display:block;margin:0 0 10px;" />
      <h1>Scan-IQ</h1>
      <div class="meta">Printed: ${esc(printedAt)}</div>
      <table>
        <thead><tr><th>Date</th><th>Time</th><th>ShortSupportNumber</th><th>SupportNo</th><th>Area</th><th>Rev</th><th>Sent to</th></tr></thead>
        <tbody>${body}</tbody>
      </table>
      <script>window.onload = function(){ window.print(); }</script>
    </body></html>`;

  const w = window.open("", "_blank");
  if (!w) {
    toast.error("Popup blocked — allow popups to print");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

export default ScanIqPage;
